import React from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Loader, Sparkles, Info } from 'lucide-react';
import type { User } from 'firebase/auth';
import {
  StructuredEssayData,
  StructuredWritingData,
  WritingTaskType,
} from '@/types/writing';
import { QuestionType, QuestionNote } from '@/types/task-bank';

interface ScoreButtonProps {
  isProUser: boolean;
  isScoring: boolean;
  prompt: string;
  currentEssayId: string | null;
  user: User | null | undefined;
  taskType?: WritingTaskType;
  questionType?: QuestionType | '';
  questionNote?: QuestionNote | '';
  canScore: boolean;
  requirements: string[];
  essayData: StructuredEssayData | StructuredWritingData;
  fetchNewScore: (
    prompt: string,
    essayData: StructuredEssayData | StructuredWritingData,
    taskType?: WritingTaskType,
    questionType?: QuestionType | '',
    questionNote?: QuestionNote | ''
  ) => void;
}

export function ScoreButton({
  isScoring,
  prompt,
  essayData,
  fetchNewScore,
  taskType = 'task2',
  questionType,
  questionNote,
  canScore,
  requirements,
}: ScoreButtonProps) {
  const handleClick = () => {
    if (canScore) {
      fetchNewScore(prompt, essayData, taskType, questionType, questionNote);
    }
  };

  let buttonContent;

  if (isScoring) {
    buttonContent = (
      <>
        <Loader className='h-4 w-4 animate-spin' />
        <span>Scoring...</span>
      </>
    );
  } else {
    buttonContent = (
      <>
        <Sparkles className='h-4 w-4' />
        <span>{taskType === 'task1' ? 'Score Report' : 'Score Essay'}</span>
      </>
    );
  }

  return (
    <div className='flex items-center gap-1 flex-wrap sm:flex-nowrap'>
      <Button
        variant={canScore ? 'default' : 'outline'}
        size='sm'
        className='gap-2 min-w-[120px] sm:min-w-[unset]'
        onClick={handleClick}
        disabled={isScoring || !canScore}
      >
        {buttonContent}
      </Button>

      {!canScore && requirements.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 rounded-full p-0 flex-shrink-0'
            >
              <Info className='h-4 w-4' />
              <span className='sr-only'>View requirements</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[280px] sm:w-80'>
            <h4 className='font-medium mb-2'>
              Requirements to score your{' '}
              {taskType === 'task1' ? 'report' : 'essay'}:
            </h4>
            <ul className='list-disc pl-4'>
              {requirements.map((req, i) => (
                <li key={i} className='mb-1 text-sm'>
                  {req}
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
